import React from 'react';
import { classNames } from '../../lib/utils';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}

export function Card({ children, className, padding = true }: CardProps) {
  return (
    <div
      className={classNames(
        'bg-white rounded-xl border border-gray-200 shadow-sm',
        padding ? 'p-6' : '',
        className
      )}
    >
      {children}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  subValue?: string;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray';
  trend?: { value: number; label: string };
}

const colorClasses = {
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600', text: 'text-blue-700' },
  green: { bg: 'bg-green-50', icon: 'text-green-600', text: 'text-green-700' },
  red: { bg: 'bg-red-50', icon: 'text-red-600', text: 'text-red-700' },
  yellow: { bg: 'bg-yellow-50', icon: 'text-yellow-600', text: 'text-yellow-700' },
  purple: { bg: 'bg-purple-50', icon: 'text-purple-600', text: 'text-purple-700' },
  gray: { bg: 'bg-gray-50', icon: 'text-gray-600', text: 'text-gray-700' },
};

export function StatCard({ label, value, subValue, icon, color = 'blue', trend }: StatCardProps) {
  const colors = colorClasses[color];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        {icon && (
          <div className={classNames('w-10 h-10 rounded-lg flex items-center justify-center', colors.bg)}>
            <span className={classNames('w-5 h-5', colors.icon)}>{icon}</span>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className={classNames('text-2xl font-bold', colors.text)}>{value}</p>
        {subValue && <p className="text-xs text-gray-500">{subValue}</p>}
        {trend && (
          <p className={classNames(
            'text-xs font-medium',
            trend.value >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {trend.value >= 0 ? '+' : ''}{trend.value}% {trend.label}
          </p>
        )}
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}

export function SectionHeader({ title, description, actions }: SectionHeaderProps) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={classNames('border-gray-200', className)} />;
}

interface BadgeProps {
  children: React.ReactNode;
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray';
}

const badgeColors = {
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  purple: 'bg-purple-100 text-purple-700',
  gray: 'bg-gray-100 text-gray-700',
};

export function Badge({ children, color = 'gray' }: BadgeProps) {
  return (
    <span className={classNames('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', badgeColors[color])}>
      {children}
    </span>
  );
}

export function LoadingSpinner({ className }: { className?: string }) {
  return (
    <div className={classNames('flex items-center justify-center py-12', className)}>
      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
    </div>
  );
}

export function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-4">
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

interface EmptyStateProps {
  title?: string;
  message: string;
  actions?: React.ReactNode;
  className?: string;
}

export function EmptyState({ title, message, actions, className }: EmptyStateProps) {
  return (
    <div className={classNames('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {title && <h3 className="text-base font-semibold text-gray-800">{title}</h3>}
      <p className={classNames('max-w-xl text-sm text-gray-500', title ? 'mt-2' : '')}>{message}</p>
      {actions && <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}
